import { useState, useEffect, useCallback } from "react";
import { useCustomerAuth } from "../components/CustomerLayout";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Trash2, Plus, Minus, Loader2, ShoppingBag, ArrowRight, MessageCircle, PackageCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerCart() {
  const { getAuthHeaders, userId, devise, refreshCounts } = useCustomerAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(null);

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/customer/cart`, getAuthHeaders());
      setItems(res.data.items || []);
    } catch { toast.error("Erreur chargement panier"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const updateQty = async (itemId, newQty) => {
    try {
      if (newQty <= 0) {
        await axios.delete(`${API}/api/customer/cart/${itemId}`, getAuthHeaders());
        setItems(prev => prev.filter(i => i.id !== itemId));
        toast.success("Article retiré");
      } else {
        await axios.put(`${API}/api/customer/cart/${itemId}`, { quantity: newQty }, getAuthHeaders());
        setItems(prev => prev.map(i => i.id === itemId ? { ...i, quantity: newQty } : i));
      }
      refreshCounts?.();
    } catch { toast.error("Erreur"); }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/api/customer/cart/${itemId}`, getAuthHeaders());
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Article retiré");
      refreshCounts?.();
    } catch { toast.error("Erreur"); }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/api/customer/checkout`, { message: message || null }, getAuthHeaders());
      setOrderSuccess(res.data);
      setItems([]);
      refreshCounts?.();
      toast.success("Commande validée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la validation");
    } finally { setCheckoutLoading(false); }
  };

  const total = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const currency = devise || "€";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  // Order success state
  if (orderSuccess) {
    return (
      <div className="max-w-md mx-auto text-center py-12 px-4" data-testid="checkout-success">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <PackageCheck className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold mb-2">Commande validée !</h2>
        <p className="text-muted-foreground mb-1">Commande n° <span className="font-semibold">{orderSuccess.order_number}</span></p>
        <p className="text-sm text-muted-foreground mb-6">La boutique va traiter votre commande. Vous pouvez suivre son statut dans vos commandes.</p>
        <div className="flex flex-col gap-3">
          <Link to={`/vitrine/${userId}/espace/orders`}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1A1714] text-white font-medium text-sm hover:bg-[#2E2820] transition-colors"
            data-testid="goto-orders-btn">
            Voir mes commandes <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to={`/vitrine/${userId}`}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-[#E8E2D9] text-[#1A1714] font-medium text-sm hover:bg-[#FAF8F5] transition-colors">
            Continuer mes achats
          </Link>
        </div>
      </div>
    );
  }

  // Empty cart
  if (items.length === 0) {
    return (
      <div className="text-center py-16 px-4" data-testid="empty-cart">
        <div className="w-16 h-16 rounded-full bg-[#FAF8F5] flex items-center justify-center mx-auto mb-4">
          <ShoppingCart className="w-8 h-8 text-[#C4BAA8]" />
        </div>
        <h2 className="text-lg font-semibold mb-2">Votre panier est vide</h2>
        <p className="text-sm text-muted-foreground mb-6">Parcourez la boutique pour ajouter des articles</p>
        <Link to={`/vitrine/${userId}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1A1714] text-white font-medium text-sm hover:bg-[#2E2820] transition-colors"
          data-testid="browse-shop-btn">
          <ShoppingBag className="w-4 h-4" /> Voir la boutique
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-2" data-testid="cart-page">
      <h1 className="text-xl font-bold mb-4">Mon panier ({items.length} article{items.length > 1 ? 's' : ''})</h1>

      {/* Cart items */}
      <div className="space-y-3 mb-6">
        {items.map(item => (
          <div key={item.id} className="flex gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-white"
            data-testid={`cart-item-${item.id}`}>
            {/* Image */}
            <div className="w-20 h-20 rounded-lg bg-[#FAF8F5] overflow-hidden flex-shrink-0">
              {item.image ? (
                <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-[#C4BAA8]" />
                </div>
              )}
            </div>
            {/* Details */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm truncate">{item.product_name}</h3>
              <p className="text-xs text-muted-foreground">
                {[item.size, item.color].filter(Boolean).join(" · ") || "—"}
              </p>
              <p className="text-sm font-bold mt-1" style={{ color: '#9C7B4E' }}>
                {item.unit_price?.toLocaleString('fr-FR')} {currency}
              </p>
            </div>
            {/* Qty + Remove */}
            <div className="flex flex-col items-end justify-between">
              <button onClick={() => removeItem(item.id)}
                className="p-1 rounded-md hover:bg-red-50 text-[#C4BAA8] hover:text-red-500 transition-colors"
                data-testid={`remove-item-${item.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQty(item.id, item.quantity - 1)}
                  className="w-7 h-7 rounded-full border border-[#E8E2D9] flex items-center justify-center hover:bg-[#FAF8F5] transition-colors"
                  data-testid={`qty-minus-${item.id}`}>
                  <Minus className="w-3 h-3" />
                </button>
                <span className="text-sm font-semibold w-5 text-center">{item.quantity}</span>
                <button onClick={() => updateQty(item.id, item.quantity + 1)}
                  className="w-7 h-7 rounded-full border border-[#E8E2D9] flex items-center justify-center hover:bg-[#FAF8F5] transition-colors"
                  data-testid={`qty-plus-${item.id}`}>
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Message */}
      <div className="mb-4">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
          Message pour la boutique (optionnel)
        </label>
        <div className="relative">
          <MessageCircle className="absolute left-3 top-3 w-4 h-4 text-[#C4BAA8]" />
          <textarea value={message} onChange={e => setMessage(e.target.value)}
            placeholder="Ex: Livraison souhaitée samedi..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E8E2D9] bg-[#FAF8F5] text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#9C7B4E]"
            rows={2} data-testid="checkout-message" />
        </div>
      </div>

      {/* Total + Checkout */}
      <div className="rounded-xl border border-[#E8E2D9] bg-white p-4 space-y-3" data-testid="cart-summary">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Sous-total ({items.reduce((s, i) => s + i.quantity, 0)} articles)</span>
          <span className="font-semibold">{total.toLocaleString('fr-FR')} {currency}</span>
        </div>
        <div className="h-px bg-[#E8E2D9]" />
        <div className="flex justify-between">
          <span className="font-bold">Total</span>
          <span className="font-bold text-lg" style={{ color: '#9C7B4E' }}>{total.toLocaleString('fr-FR')} {currency}</span>
        </div>
        <button onClick={handleCheckout} disabled={checkoutLoading}
          className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
          style={{ backgroundColor: '#1A1714' }}
          data-testid="checkout-btn">
          {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
          Valider ma commande
        </button>
      </div>
    </div>
  );
}
